import { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  DialogContent,
  Icons,
  DialogActions,
  ScrollableDialogContent,
  Skeleton,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';
import { InstallableKentFeature, KentFeature } from '@cast/types-kent';

import { StepContainer } from 'hooks/dialogs/cluster/_components/new-onboarding';
import {
  getClusterFeaturesQueryKey,
  useGetClusterFeaturesQuery,
} from 'hooks/queries/kent/useGetClusterFeaturesQuery';
import { getClusterOverviewQueryKey } from 'hooks/queries/kent/useGetClusterOverview';
import { notReachable } from 'utils/types/not-reachable';

import { CustomizeFeaturesSection } from './_components/CustomizeFeaturesSection';
import { DialogHeader, DialogHeaderProps } from './_components/DialogHeader';
import { InstallScript } from '../../components/InstallScript';
import { filterInstallableKentFeatures } from '../../utils';
import { useKentSelectedFeatures } from '../_hooks/useKentSelectedFeatures';

import { FailedToLoad } from '@cast/shared-messages-ui';

const StyledDialogActions = ({ children }: React.PropsWithChildren) => {
  return (
    <DialogActions
      sx={{ borderTop: tokens.theme.border.structural.neutral, py: 16 }}
    >
      {children}
    </DialogActions>
  );
};

type ContentProps = {
  cluster: Cluster;
  kentFeatures?: KentFeature[];
  featureBeingInstalled?: InstallableKentFeature['type'];
  onClose: () => void;
  onRunTheScript: () => void;
  onCancel: () => void;
  onBack?: () => void;
};

const Content = ({
  cluster,
  featureBeingInstalled,
  kentFeatures,
  onClose,
  onBack,
  onRunTheScript,
  onCancel,
}: ContentProps) => {
  const [isExpanded, setExpanded] = useState(false);

  const { selectedFeatures, setSelectedFeatures } = useKentSelectedFeatures(
    featureBeingInstalled
  );
  const {
    selectedFeatures: draftSelectedFeatures,
    setSelectedFeatures: setDraftSelectedFeatures,
    toggleFeature,
    toggleKentSuite,
  } = useKentSelectedFeatures(featureBeingInstalled);

  const installableKentFeatures = useMemo(
    () => filterInstallableKentFeatures(kentFeatures || []),
    [kentFeatures]
  );

  const queryClient = useQueryClient();

  return (
    <>
      <DialogHeader cluster={cluster} onClose={onClose} />
      <ScrollableDialogContent>
        <Stack gap={4}>
          <Stack>
            <Typography variant="h6">Enable automation</Typography>
            <Typography variant="P12R" color="textSecondary">
              Enable {CAST_AI}&apos;s monitoring and automation features in your
              cluster. Run the script below to deploy the required components.
            </Typography>
          </Stack>
          <CustomizeFeaturesSection
            selectedFeatures={draftSelectedFeatures}
            isExpanded={isExpanded}
            featureBeingInstalled={featureBeingInstalled}
            kentFeatures={installableKentFeatures}
            onToggleSection={() => setExpanded((prev) => !prev)}
            onToggleFeature={toggleFeature}
            onToggleKentSuite={toggleKentSuite}
          />
          {!isExpanded && (
            <InstallScript
              cluster={cluster}
              selectedFeatures={selectedFeatures}
              sx={{
                backgroundColor: 'grey.100',
                mx: -24,
                px: 24,
                py: 20,
                mb: -20,
              }}
            />
          )}
        </Stack>
      </ScrollableDialogContent>
      <StyledDialogActions>
        {isExpanded ? (
          <>
            <Button
              testId="discard-button"
              variant="secondary"
              onClick={() => {
                setExpanded(false);
                setDraftSelectedFeatures(selectedFeatures);
              }}
            >
              Discard
            </Button>
            <Button
              testId="confirm-selection-button"
              onClick={() => {
                setExpanded(false);
                setSelectedFeatures(draftSelectedFeatures);
              }}
            >
              Confirm selection
            </Button>
          </>
        ) : (
          <>
            {!!onBack && (
              <Button
                variant="ghost"
                startIcon={<Icons.ArrowLeft />}
                sx={{
                  marginRight: 'auto',
                }}
                onClick={onBack}
              >
                Back
              </Button>
            )}
            <Button
              testId="cancel-button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              testId="run-script-button"
              onClick={() => {
                if (cluster) {
                  queryClient.invalidateQueries({
                    queryKey: getClusterOverviewQueryKey(cluster.id),
                  });
                  queryClient.invalidateQueries({
                    queryKey: getClusterFeaturesQueryKey(cluster.id),
                  });
                }

                onRunTheScript();
              }}
            >
              I ran the script
            </Button>
          </>
        )}
      </StyledDialogActions>
    </>
  );
};

type SkeletonContentProps = DialogHeaderProps;

const SkeletonContent = ({ cluster, onClose }: SkeletonContentProps) => {
  return (
    <>
      <DialogHeader cluster={cluster} onClose={onClose} />
      <DialogContent>
        <Stack gap={8}>
          <Skeleton variant="rounded" width="100%" height={48} />
          <Skeleton variant="rounded" width="100%" height={150} />
        </Stack>
      </DialogContent>
      <StyledDialogActions>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </StyledDialogActions>
    </>
  );
};

type ErrorContentProps = DialogHeaderProps & {
  refetch: () => void;
};

const ErrorContent = ({ cluster, onClose, refetch }: ErrorContentProps) => {
  return (
    <>
      <DialogHeader cluster={cluster} onClose={onClose} />
      <DialogContent>
        <FailedToLoad refresh={refetch} />
      </DialogContent>
    </>
  );
};

export type InstallKentScriptStepDialogContentProps = Omit<
  ContentProps,
  'kentFeatures'
>;

export const InstallKentScriptStepDialogContent = ({
  cluster,
  featureBeingInstalled,
  onClose,
  onRunTheScript,
  onCancel,
  onBack,
}: InstallKentScriptStepDialogContentProps) => {
  const query = useGetClusterFeaturesQuery({
    clusterId: cluster.id,
  });

  return (
    <StepContainer data-testid="install-kent-script-step-dialog-content">
      {(() => {
        switch (query.status) {
          case 'pending':
            return <SkeletonContent cluster={cluster} onClose={onClose} />;
          case 'error':
            return (
              <ErrorContent
                cluster={cluster}
                onClose={onClose}
                refetch={query.refetch}
              />
            );
          case 'success':
            return (
              <Content
                cluster={cluster}
                featureBeingInstalled={featureBeingInstalled}
                kentFeatures={query.data}
                onClose={onClose}
                onRunTheScript={onRunTheScript}
                onCancel={onCancel}
                onBack={onBack}
              />
            );
          default:
            return notReachable(query);
        }
      })()}
    </StepContainer>
  );
};
